import React, { useEffect, useState, useRef } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, List, ListItem, Divider, Button, Stack, Paper, CircularProgress, Tabs, Tab, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux-toolkit/store/stateType';
import { User } from '../../redux-toolkit/reducers/userReducer';
import { Employee } from '../../redux-toolkit/reducers/allEmployeeReducer';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { createGoal, fetchCurrentGoals, updateGoal } from '../../services/goalService';
import GoalsCard from './GoalsCard';
import GoalModal, { GoalModalHandle } from './TeamGoalsModal';
import SetMuiAlert from '../Alert/SetMuiAlert';
import constants, { ALLOWED_KEYS } from '../../shared/constants';
import GroupIcon from '@mui/icons-material/Group';

interface RecordType {
    goalId: string;
    goalStartDate: number;
    goalEndDate: number;
    goals: string;
    organisationId: string;
    userName: string;
    employeeType: string;
    leadId: string;
}

interface RelatedParam {
    keyResult: string;
    endDate: string;
    score: string;
}

interface Objective {
    objective: string;
    relatedParams: RelatedParam[];
}

interface Goal {
    okr: string;
    objectives: Objective[];
}

interface EmployeeDetail {
    employeeName: string;
    goals: Goal[];
}

interface EmployeeWithGoals extends Employee {
    leadId: string;
    goalStartDate: string;
    goalEndDate: string;
    goals: string;
    goalId: string;
}

const findProjectOfEmployee = (employeeName: string, projects: any[]): string[] => {
    if (!employeeName || !Array.isArray(projects)) return [];

    return projects
        .filter((project: any) => project?.employeesWorking?.includes(employeeName))
        .map((project: any) => project?.projectName || '')
        .filter(Boolean);
};

const leadAssociatedMembers = (userName: string, employeeType: string, projects: any[], employees: Employee[], user: User) => {

    if (!userName || !Array.isArray(projects) || !Array.isArray(employees)) {
        return { membersAssociated: [], filteredProjects: [] };
    }
    const userEmployee = employees.find(employee =>
        employee?.firstName && employee?.lastName &&
        `${user?.firstName} ${user?.lastName}` === `${employee.firstName} ${employee.lastName}`);

    if (constants.EMPLOYEE_TYPES.ADMIN == employeeType) {
        return { membersAssociated: employees, filteredProjects: [] };
    }
    if (constants.EMPLOYEE_TYPES.TEAM_LEAD !== employeeType && constants.EMPLOYEE_TYPES.PROJECT_MANAGER !== employeeType) {
        if (userEmployee) return { membersAssociated: [userEmployee], filteredProjects: [] };
    }

    const filteredProjects = projects.filter((project: any) => {
        const userIsLeadOrManager = project?.projectLead === userName || project?.projectManger === userName;
        const projectIsActive = project?.projectStatus === true;
        const hasEmployees = project?.employeesWorking && project?.employeesWorking.trim() !== '';
        return userIsLeadOrManager && projectIsActive && hasEmployees;
    });

    const membersAssociatedStr: any = [];
    filteredProjects.forEach((project: any) => {
        if (project?.employeesWorking) {
            membersAssociatedStr.push(project.employeesWorking);
        }
    });

    const membersAssociated = membersAssociatedStr.flatMap((memberString: string) => {
        if (typeof memberString === 'string' && memberString.trim()) {
            return memberString.split(',').map((member: string) => member.trim()).filter(Boolean);
        }
        return [];
    });

    const distinctMembers = Array.from(new Set(membersAssociated));
    const employeeObjArray: EmployeeWithGoals[] = [];

    distinctMembers.forEach((member: any) => {
        const employee = employees.find(employee =>
            employee?.firstName && employee?.lastName &&
            member === `${employee.firstName} ${employee.lastName}`
        );
        if (employee && employee?.employeeStatus) {
            employeeObjArray.push({
                ...employee,
                goalStartDate: '',
                goalEndDate: '',
                leadId: userName,
                goals: '',
                goalId: ''
            });
        }
    });
    if (employeeObjArray.length === 0) {
        if (userEmployee) {
            employeeObjArray.push({
                ...userEmployee,
                goalStartDate: '',
                goalEndDate: '',
                leadId: userName,
                goals: '',
                goalId: ''
            });
        }
    }
    employeeObjArray.sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });

    return { membersAssociated: employeeObjArray, filteredProjects: filteredProjects };
};

const filterRecordsBasedOnEmployee = (employeeName: string, records: any[], members: any[]) => {
    if (!employeeName || !Array.isArray(records) || !Array.isArray(members)) {
        return [];
    }

    const reqMember = members.find((member: any) =>
        member?.firstName && member?.lastName &&
        `${member.firstName} ${member.lastName}` === employeeName
    );

    if (!reqMember?.userName) return [];

    const userName = reqMember.userName;

    return records
        .filter((record: any) => record?.userName === userName)
        .sort((a, b) => (b?.goalStartDate || 0) - (a?.goalStartDate || 0));
};

const calculateThreeMonthsAhead = (date: Date): Date => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        date = new Date();
    }
    return new Date(date.getFullYear(), date.getMonth() + 3, date.getDate());
};

const groupRecordsByYear = (records: any[]) => {
    if (!Array.isArray(records)) return {};

    return records.reduce((acc: any, record: any) => {
        const timestamp = record?.goalStartDate;
        if (!timestamp) return acc;

        const year = new Date(timestamp * 1000).getFullYear();
        if (isNaN(year)) return acc;

        if (!acc[year]) acc[year] = [];
        acc[year].push(record);
        return acc;
    }, {});
};

const filterObjectByKeys = (obj: any, allowedKeys: string[]) => {
    if (!obj || !Array.isArray(allowedKeys)) return {};

    return Object.keys(obj)
        .filter((key) => allowedKeys.includes(key))
        .reduce((filteredObj, key) => {
            return { ...filteredObj, [key]: obj[key] };
        }, {});
};

const findDesignation = (employeeName: string, employees: Employee[]): string => {
    if (!employeeName || !Array.isArray(employees)) return '';

    const foundEmployee = employees.find((employee: Employee) =>
        employee?.firstName && employee?.lastName &&
        `${employee.firstName} ${employee.lastName}` === employeeName
    );
    return foundEmployee?.designation || '';
};

const getProjectActiveMembers = (project: any, employees: Employee[]) => {
    return project?.employeesWorking ? (
        project.employeesWorking.split(',').map((employee: string) => {
            const trimmedEmployee = employee.trim();
            if (!trimmedEmployee) return null;

            const foundEmployee = employees.find((emp: Employee) => {
                if (emp?.firstName && emp?.lastName) {
                    return `${emp.firstName} ${emp.lastName}` === trimmedEmployee && emp?.employeeStatus;
                } else {
                    return false;
                }
            }

            );
            return foundEmployee ? trimmedEmployee : null;
        }).filter(Boolean)
    ) : [];
};

const TeamGoals: React.FC = () => {
    const employees = useSelector((state: RootState) => state.employees)?.filter((emp: Employee) => emp?.employeeStatus) || [];
    const projects: any[] = useSelector((state: RootState) => state.projects) || [];
    const user: User = useSelector((state: RootState) => state.user);
    const goalsModalRef = useRef<GoalModalHandle>(null);
    
    const { membersAssociated, filteredProjects } = leadAssociatedMembers(
        user?.userName || '',
        user?.employeeType || '',
        projects,
        employees,
        user
    );

    const [records, setRecords] = useState<RecordType[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [selectEditRecord, setSelectEditRecord] = useState<any>('');
    const [selectedEmployee, setSelectedEmployee] = useState<any>('');
    const [openModal, setOpenModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

    const initialStartDate = new Date();
    const initialEndDate = calculateThreeMonthsAhead(initialStartDate);

    const [goalStartDate, setGoalStartDate] = useState<Date | null>(null);
    const [goalEndDate, setGoalEndDate] = useState<Date | null>(null);
    const [employeeGoals, setEmployeeGoals] = useState<any>(null);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackBarSeverity, setSnackBarSeverity] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedYearTab, setSelectedYearTab] = useState<{ [key: number]: number }>({});

    const handleYearTabChange = (empIndex: number, newValue: number) => {
        setSelectedYearTab(prev => ({
            ...prev,
            [empIndex]: newValue
        }));
    };

    const handleOpenCreate = (employee: any) => {
        if (!employee) return;
        setSelectedEmployee(employee);
        setModalMode('create');
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleEdit = (record: any, employee: string, index: number) => {
        if (!record || !employee) return;

        setSelectEditRecord(record);
        setSelectedEmployee(employee);
        setModalMode('edit');
        setEmployeeGoals(record?.goals);

        const startDate = record?.goalStartDate ? new Date(record.goalStartDate * 1000) : new Date();
        const endDate = record?.goalEndDate ? new Date(record.goalEndDate * 1000) : calculateThreeMonthsAhead(startDate);

        setGoalStartDate(startDate);
        setGoalEndDate(endDate);
        setOpenModal(true);
    };

    const handleSubmitGoals = async (goalData: any) => {

        try {
            if (!goalData) {
                setShowAlert(true);
                setSnackbarMessage('Goal data is required!');
                setSnackBarSeverity('error');
                return;
            }

            if (!goalStartDate || !goalEndDate) {
                setShowAlert(true);
                setSnackbarMessage('Goal start and end date is required!');
                setSnackBarSeverity('error');
                return;
            }
            // validate total goal score
            const totalScore = goalData?.reduce((acc: number, obj: any) => acc + (Number(obj?.score) || 0), 0);
            if (totalScore !== 100) {
                setShowAlert(true);
                setSnackbarMessage('Total goals score should be 100!');
                setSnackBarSeverity('error');
                return;
            }
            setSubmitLoading(true);

            if (modalMode === 'create') {
                const foundEmployee = membersAssociated.find(employee =>
                    employee?.firstName && employee?.lastName &&
                    `${employee.firstName} ${employee.lastName}` === selectedEmployee
                );

                if (!foundEmployee) {
                    setShowAlert(true);
                    setSnackbarMessage('Employee not found!');
                    setSnackBarSeverity('error');
                    return;
                }

                const updatedEmployeeData = {
                    ...foundEmployee,
                    goalStartDate: goalStartDate ? new Date(goalStartDate).getTime() / 1000 : 0,
                    goalEndDate: goalEndDate ? new Date(goalEndDate).getTime() / 1000 : 0,
                    goals: goalData,
                    organisationId: 'techpearl',
                    goalId: '',
                };

                const filteredDataObj = filterObjectByKeys(updatedEmployeeData, ALLOWED_KEYS) as RecordType;
                const response = await createGoal(filteredDataObj);
                setSnackbarMessage('Goal successfully created!');
            }
            else if (modalMode === 'edit') {
                const updatedEmployeeData = {
                    ...selectEditRecord,
                    goalStartDate: goalStartDate ? new Date(goalStartDate).getTime() / 1000 : 0,
                    goalEndDate: goalEndDate ? new Date(goalEndDate).getTime() / 1000 : 0,
                    goals: goalData,
                    organisationId: 'techpearl',
                };

                const filteredDataObj = filterObjectByKeys(updatedEmployeeData, ALLOWED_KEYS) as RecordType;
                const response = await updateGoal(filteredDataObj);
                setSnackbarMessage('Goal successfully updated!');
            }

            if (user?.userName) {
                const membersResponse = await fetchCurrentGoals(user.userName);
                setRecords(membersResponse?.data?.res?.Items || []);
            }

            setSnackBarSeverity('success');
            setShowAlert(true);
            handleCloseModal();
            setModalMode('create');
            setGoalStartDate(null);
            setGoalEndDate(null);
            goalsModalRef?.current?.resetObjectives();
        } catch (error) {
            console.error(
                modalMode === 'create' ? 'Error creating goal:' : 'Error updating goal:',
                error
            );
            setShowAlert(true);
            setSnackbarMessage(modalMode === 'create' ? 'Error creating goal!' : 'Error updating goal!');
            setSnackBarSeverity('error');
        } finally {
            setSubmitLoading(false);
            setTimeout(() => {
                setShowAlert(false);
            }, 2000);
        }
    };

    useEffect(() => {
        const fetchGoalsData = async () => {
            if (!user?.userName) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const membersResponse = await fetchCurrentGoals(user.userName);
                setRecords(membersResponse?.data?.res?.Items || []);
            } catch (error) {
                console.error('Error fetching goals:', error);
                setRecords([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGoalsData();
    }, [user?.userName]);

    if (loading) {
        return (
            <Stack justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Stack>
        );
    }

    return (

        <Box sx={{ paddingBottom: '20px' }}>
            {membersAssociated.map((employee: any, empIndex: number) => {
                const trimmedEmployee = `${employee?.firstName} ${employee?.lastName}`;
                if (!trimmedEmployee) return null;

                // Filter records and group them by year
                const employeeRecords = filterRecordsBasedOnEmployee(
                    trimmedEmployee,
                    records,
                    membersAssociated
                );

                const groupedByYear = groupRecordsByYear(employeeRecords);

                return (
                    <Accordion key={empIndex} elevation={2} sx={{ borderRadius: '10px', marginTop: 2 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`employee-panel-${empIndex}-content`}
                            id={`employee-panel-${empIndex}-header`}
                            sx={{ minHeight: 100, '&.Mui-expanded': { minHeight: 80 } }}
                        >
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={'space-between'}
                                width={'95%'}
                            >
                                <Stack direction={'row'} spacing={1} width="40%">
                                    <Typography variant="h6" color="black" fontWeight='bold'>Employee:</Typography>
                                    <Typography variant="h6" color="black">{trimmedEmployee}</Typography>
                                </Stack>
                                <Stack direction={'row'} spacing={1} width="40%">
                                    <Typography variant="h6" fontWeight="bold">Designation: </Typography>
                                    <Typography variant="h6">
                                        {findDesignation(trimmedEmployee, membersAssociated) || 'N/A'}
                                    </Typography>
                                </Stack>
                                <Stack>
                                    <Button
                                        color="secondary"
                                        size="small"
                                        variant="contained"
                                        startIcon={<AddOutlinedIcon />}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenCreate(trimmedEmployee);
                                        }}
                                    >
                                        Add Goal
                                    </Button>
                                </Stack>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            {Object.keys(groupedByYear).length > 0 ? (
                                (() => {
                                    const sortedYears = Object.keys(groupedByYear).sort((a, b) => Number(b) - Number(a));
                                    const currentTabIndex = selectedYearTab[empIndex] || 0;
                                    const selectedYear = sortedYears[currentTabIndex];

                                    return (
                                        <Box sx={{ width: '100%' }}>
                                            <Tabs
                                                value={currentTabIndex}
                                                onChange={(event, newValue) => handleYearTabChange(empIndex, newValue)}
                                                aria-label={`year-tabs-${empIndex}`}
                                                sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}
                                                indicatorColor='secondary'
                                                textColor='secondary'
                                            >
                                                {sortedYears.map((year, yearIndex) => (
                                                    <Tab key={year} label={year} value={yearIndex} />
                                                ))}
                                            </Tabs>

                                            <Box sx={{ px: 3 }}>
                                                {selectedYear && Array.isArray(groupedByYear[selectedYear]) &&
                                                    groupedByYear[selectedYear].map((record: any, recordIndex: number) => (
                                                        <Paper key={recordIndex} sx={{ padding: 2, marginBottom: 3, borderRadius: 2, boxShadow: '0 0 8px rgba(0, 0, 0, 0.2)' }} elevation={0}>
                                                            <Stack
                                                                direction={'row'}
                                                                alignItems={'center'}
                                                                justifyContent={'space-between'}
                                                            >
                                                                <Stack direction="row" spacing={5} justifyContent="flex-start">
                                                                    <Typography variant="body1" gutterBottom>
                                                                        <strong>Goal Start Date:</strong>{'  '}
                                                                        {record?.goalStartDate
                                                                            ? new Date(record.goalStartDate * 1000).toLocaleDateString()
                                                                            : 'N/A'}
                                                                    </Typography>

                                                                    <Typography variant="body1" gutterBottom>
                                                                        <strong>Goal End Date:</strong>{'  '}
                                                                        {record?.goalEndDate
                                                                            ? new Date(record.goalEndDate * 1000).toLocaleDateString()
                                                                            : 'N/A'}
                                                                    </Typography>
                                                                </Stack>

                                                                <Stack>
                                                                    <Button
                                                                        variant="contained"
                                                                        color="secondary"
                                                                        fullWidth
                                                                        size='small'
                                                                        onClick={() => handleEdit(record, trimmedEmployee, recordIndex)}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                </Stack>
                                                            </Stack>
                                                            <GoalsCard goalsData={record?.goals} />
                                                        </Paper>
                                                    ))
                                                }
                                                {(!selectedYear || !Array.isArray(groupedByYear[selectedYear]) || groupedByYear[selectedYear].length === 0) && (
                                                    <Typography variant="body2" color="gray" textAlign="center" padding={2}>
                                                        No goals found for {selectedYear || 'this year'}.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })()
                            ) : (
                                <Typography variant="body2" color="gray" textAlign="center" padding={2}>
                                    No goals found for this employee.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            }).filter(Boolean)}

            <GoalModal
                open={openModal}
                onClose={handleCloseModal}
                mode={modalMode}
                employeeName={selectedEmployee}
                goalsData={employeeGoals}
                onSubmit={handleSubmitGoals}
                goalStartDate={goalStartDate}
                goalEndDate={goalEndDate}
                setGoalStartDate={setGoalStartDate}
                setGoalEndDate={setGoalEndDate}
                submitLoading={submitLoading}
            />

            <SetMuiAlert
                message={snackbarMessage}
                severity={snackBarSeverity}
                open={showAlert}
                onClose={() => setShowAlert(false)}
            />
        </Box>
    );
};

export default TeamGoals;